import { useState, useEffect } from 'react';
import Header from '../../../components/Header';
import Footer from '../../../components/Footer';
import data from '../../../data/target-sales.json';

// JSON-LD Structured Data for Target Sales
const jsonLdData = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Target Sales",
  "alternateName": "Automação e Microgerenciamento Comercial",
  "url": "https://www.targetsoftware.com.br/sales",
  "provider": {
    "@type": "Organization",
    "name": "Target Software",
    "url": "https://www.targetsoftware.com.br",
    "logo": "https://www.targetsoftware.com.br/assets/img/logo.png"
  },
  "description": "O Target Sales é uma solução completa para automação e microgerenciamento comercial, com pipeline, alertas inteligentes, análises profundas, integração de agenda e inteligência artificial aplicada aos indicadores comerciais.",
  "serviceType": "Automação Comercial e Gestão de Pipeline",
  "areaServed": {
    "@type": "Country",
    "name": "Brasil"
  },
  "audience": {
    "@type": "BusinessAudience",
    "businessSize": "SMB/Enterprise",
    "industry": "Seguros, Telecom, Serviços Financeiros, Varejo, Indústria, Tecnologia"
  },
  "offers": {
    "@type": "Offer",
    "price": "Sob consulta",
    "priceCurrency": "BRL",
    "availability": "https://schema.org/InStock"
  },
  "brand": {
    "@type": "Brand",
    "name": "Target Software"
  },
  "hasPart": [
    {
      "@type": "Service",
      "name": "Alertas Inteligentes",
      "description": "Alertas automáticos enviados via WhatsApp, e-mail, aplicativo e notificações internas."
    },
    {
      "@type": "Service",
      "name": "Análise de Eventos",
      "description": "Análises atualizadas com base em eventos sistêmicos e registros de operação."
    },
    {
      "@type": "Service",
      "name": "Pipeline Comercial",
      "description": "Visualização completa das oportunidades comerciais em formato de pipeline."
    },
    {
      "@type": "Service",
      "name": "Planejamento Comercial",
      "description": "Visão detalhada dos resultados por estrutura, região e indicadores estratégicos."
    },
    {
      "@type": "Service",
      "name": "Análise Comercial",
      "description": "Raio-X dos indicadores com análises profundas e inteligência analítica."
    },
    {
      "@type": "Service",
      "name": "Inteligência Artificial",
      "description": "IA aplicada para análise de indicadores, resumos automáticos e insights comerciais."
    },
    {
      "@type": "Service",
      "name": "Integração de Agenda",
      "description": "Visão integrada de agendas e compromissos de toda a equipe comercial."
    },
    {
      "@type": "Service",
      "name": "Multi Dispositivos",
      "description": "Acesso completo para tablets e celulares para equipes operacionais e de vendas."
    }
  ],
  "keywords": [
    "automação comercial",
    "gestão de pipeline",
    "funil de vendas",
    "alertas inteligentes",
    "indicadores comerciais",
    "IA comercial",
    "produtividade comercial",
    "gestão de equipes de vendas",
    "oportunidades comerciais",
    "inteligência de vendas",
    "análise comercial",
    "planejamento comercial"
  ],
  "image": "https://www.targetsoftware.com.br/assets/img/target-sales-banner.png"
};

const TargetSalesPage = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);

  const { diferenciais, cases } = data;

  const handleContactClick = () => {
    window.location.href = '/contato';
  };

  // Add JSON-LD structured data to head
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLdData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Banner */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#077a65] via-[#4AC4AA] to-[#A3E5D5]">
        <div className="absolute inset-0">
          <img 
            src="/images/produtos/target-sales.jpg"
            alt="Target Sales"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#077a65]/80 to-[#077a65]/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center text-white">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
              <i className="ri-line-chart-line mr-2"></i>
              Automação Comercial Completa
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold leading-tight mb-6">
              Target Sales
            </h1>
            <p className="text-xl lg:text-2xl text-gray-200 max-w-4xl mx-auto mb-8 leading-relaxed">
              Automatize o planejamento comercial e o ciclo de vendas <br /> 
              de forma <strong className="text-white"> integrada com seu ambiente</strong> e 
              <strong className="text-white"> alertas automáticos</strong> <br /> 
              para realizar o micro gerenciamento das oportunidades comerciais
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="flex items-center text-lg">
                <i className="ri-check-line text-green-400 mr-2"></i>
                <span>Alertas inteligentes no whatsApp</span>
              </div>
              <div className="flex items-center text-lg">
                <i className="ri-check-line text-green-400 mr-2"></i>
                <span>Dashboards em tempo real</span>
              </div>
              <div className="flex items-center text-lg">
                <i className="ri-check-line text-green-400 mr-2"></i>
                <span>Integração com CRMs e ERPs</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Saiba Mais - Vídeo */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Conheça o <span className="text-[#077a65]">Target Sales</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Descubra como nossa solução se integra ao planejamento comercial e o ciclo de vendas, 
            proporcionando insights em tempo real e aumento de resultados.
          </p>
          <button
            onClick={() => setShowVideoModal(true)}
            className="inline-flex items-center px-8 py-4 bg-[#077a65] text-white font-semibold rounded-xl hover:bg-[#065c4f] transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap cursor-pointer">
            <i className="ri-play-circle-line text-2xl mr-3"></i>
            Assistir Vídeo de Apresentação
          </button>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Diferenciais Competitivos
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Conheça os diferenciais que fazem do Target Sales a solução ideal <br />
              para automação comercial
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {diferenciais.map((diferencial, index) => (
              <div key={index} className="group">
                <div className="bg-gray-50 rounded-2xl p-6 h-full hover:bg-[#077a65] transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer">
                  <div className="w-12 h-12 bg-[#077a65] rounded-xl flex items-center justify-center mb-4 group-hover:bg-white transition-colors duration-300">
                    <i className={`${diferencial.icon} text-2xl text-white group-hover:text-[#077a65]`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-white transition-colors duration-300">
                    {diferencial.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed group-hover:text-gray-200 transition-colors duration-300">
                    {diferencial.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cases de Sucesso */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Cases de Sucesso
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Empresas líderes que otimizam suas operações comerciais
            </p>
          </div>

          <div className="grid max-w-4xl mx-auto justify-center">
            {cases.map((caso, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-6">
                  <img 
                    src={caso.logo} 
                    alt={caso.cliente}
                    className="h-12 w-auto object-contain m-auto"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                  {caso.cliente}
                </h3>
                <p className="text-gray-600 leading-relaxed text-center">
                  {caso.descricao}
                </p>               
                <ul className="mt-4 space-y-2">
                  {caso.indicadores && caso.indicadores.map((indicador, idx) => (
                    <li key={idx} className="flex items-center text-gray-700 text-sm font-medium">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      {indicador}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-[#077a65]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Pronto para Automatizar suas Vendas?
          </h2>
          <p className="text-xl text-gray-200 mb-8 leading-relaxed">
            Entre em contato conosco e descubra como o Target Sales pode revolucionar <br />
            o planejamento comercial e o ciclo de vendas da sua empresa.
          </p>
          <button
            onClick={handleContactClick}
            className="inline-flex items-center px-8 py-4 bg-white text-[#077a65] font-semibold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-line text-xl mr-3"></i>
            Falar com Especialista
          </button>
        </div>
      </section>

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Vídeo de Apresentação</h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="bg-gray-100 rounded-xl p-8 text-center">
              <i className="ri-video-line text-4xl text-gray-400 mb-4"></i>
              <p className="text-gray-600">
                O vídeo de apresentação será disponibilizado em breve. 
                Entre em contato para mais informações.
              </p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default TargetSalesPage;